import React, {useEffect, useState} from 'react';
import {CodeSendStatuses, GenerateCSVResT, GroupDetailTableDataT} from '../../../types/codes';
import {
  AboveTableLine,
  ClearButton,
  ModalDescr,
  ResultLabel,
  SearchResultWrapper,
  TableViewSelect,
  ViewTable,
} from './styles';
import {CenteredLoader} from '../../../ui-kit/Loader';
import {TableWrapper} from '../../../ui-kit/Table';
import {
  TableViews,
  useCodesView,
  useEditingCodes,
  useGuestChangeCodes,
  usePromoCodesActionsT,
} from '../../../hooks/groups';
import {updateTargetT, useSetEmailsT} from '../../../hooks/emails';
import {Modal} from 'antd';
import {getTableColumns} from './TableColumns';
import {DownloadCSV} from './DownloadCSV';
import {useEmailPreview} from '../../../containers/Groups/EmailPreview';

export enum ModalLvl {
  closed = 'closed',
  confirm = 'confirm',
  success = 'success',
  upload = 'upload',
}

type GroupDetailTableT = {
  promoCodes?: GroupDetailTableDataT[];
  listLoading?: boolean;
  onDelete?: (id?: string) => Promise<boolean>;
  onUpdate?: usePromoCodesActionsT['onUpdate'];
  emailsData: useSetEmailsT;
  updateTarget: updateTargetT;
  searchPromoCodes: (v: string) => void;
  refetch?: () => void;
  setTableView: (view: TableViews) => void;
  tableView: TableViews;
  onSendCode: (id?: string) => Promise<boolean>;
  onSendGuest: (ids?: string[]) => Promise<boolean>;
  onDeletePromoCode?: usePromoCodesActionsT['onDelete'];
  totalCodes?: number;
  usedCodes?: number;
  downloadCSV?: () => GenerateCSVResT;
  groupId?: number;
};

export type SearchProps = {
  value: string;
  setValue: (v: string) => void;
  searchPromoCodes: (v: string) => void;
  showResult: (v: boolean) => void;
};

type SearchResultProps = SearchProps & {
  resQty?: number;
};
const tableViewOptions = [
  {label: 'Distribution View', value: TableViews.guests},
  {label: 'Codes List View', value: TableViews.codes},
];
export type emailState = Record<any, emailStateVal>;
export type emailStateVal = {key: string; email?: string; name?: string};

export const GroupDetailTable: React.FC<GroupDetailTableT> = ({
  promoCodes,
  listLoading,
  onSendCode,
  onUpdate,
  emailsData,
  updateTarget,
  searchPromoCodes,
  refetch,
  onDelete,
  tableView,
  setTableView,
  onSendGuest,
  onDeletePromoCode,
  totalCodes,
  usedCodes,
  downloadCSV,
  groupId,
}) => {
  const {emails: guestState, handleSetEmail, handleSetName, reset} = emailsData;
  const [focusedId, setFocusedId] = useState<string | undefined>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<string | undefined>('');
  const [searchValue, setSearchValue] = useState('');
  const [isSearchRes, setIsSearchRes] = useState<boolean>(false);
  const [sendModalLvl, setSendModalLvl] = useState<ModalLvl>(ModalLvl.closed);
  const [delModalLvl, setDelModalLvl] = useState<ModalLvl>(ModalLvl.closed);
  const setDelPopup = (lvl: ModalLvl) => setDelModalLvl(lvl);
  const unused = promoCodes?.[0]?.code?.unused;
  const codesState = useGuestChangeCodes(promoCodes);
  const tableViewChange = (val: any) => setTableView(val);
  const setSendPopup = (lvl: ModalLvl) => setSendModalLvl(lvl);
  const handleSend = (id?: string) => {
    setFocusedId(id);
    setSendPopup(ModalLvl.confirm);
  };
  const {loadEmailPreview} = useEmailPreview(groupId);

  const editingCodeStatus = promoCodes?.find((cod) => cod.id === editingId)?.actions.codeStatus;
  const codeStatus = promoCodes?.find((cod) => cod.id === focusedId)?.actions.codeStatus;
  const sendText = codeStatus === CodeSendStatuses.sent || codeStatus === CodeSendStatuses.queued ? 'resend' : 'send';
  const setFocusId = (id?: string) => setFocusedId(id);
  const setEditing = (id?: string) => setEditingId(id);
  const {promoState, onUpdatePromo, changePromoState} = useCodesView(
    tableView,
    promoCodes,
    editingId,
    onUpdate,
    setEditing,
  );
  const send = async (id?: string) => {
    if (!id) return false;
    setLoading(true);
    try {
      if (tableView === TableViews.guests) {
        const res = await onSendGuest?.([id]);
        if (res) refetch?.();
      }
      if (tableView === TableViews.codes) {
        const res = await onSendCode?.(id);
        if (res) refetch?.();
      }
    } catch (e) {
      console.log(e);
    }
    setLoading(false);
    setFocusedId('');
  };
  const onEditSuccess = async (id?: string) => {
    const sent = promoCodes
      ?.find((el) => el?.id === id)
      ?.code?.used?.some((el) => el?.status !== CodeSendStatuses.notsent);
    if (sent) {
      handleSend(id);
    } else {
      setFocusId('');
    }
    reset();
    setEditingId('');
  };
  const updateTargets = updateTarget(codesState.codes, false, unused, onEditSuccess);
  const {eCodeState, handleEdit, onSave} = useEditingCodes(
    promoCodes?.find((el) => el?.id === editingId),
    onUpdate,
    editingId,
    onEditSuccess,
  );

  useEffect(() => {
    if (tableView === TableViews.guests) {
      if (delModalLvl === ModalLvl.confirm) {
        Modal.confirm({
          title: `Do you want to delete this guest?`,
          content: (
            <ModalDescr>
              Once confirm, the guest will be removed from the Group, and you won’t be able to make any further edits.
            </ModalDescr>
          ),
          okText: 'Yes, proceed to delete',
          onOk: async () => {
            const res = await onDelete?.(focusedId);
            res && setDelPopup(ModalLvl.success);
          },
          onCancel: () => setDelPopup(ModalLvl.closed),
        });
      }
    }
    if (tableView === TableViews.codes) {
      if (delModalLvl === ModalLvl.confirm) {
        Modal.confirm({
          title: `Do you want to delete this promocode?`,
          content: (
            <ModalDescr>
              Once confirm, the promo will be removed from the Group, and you won’t be able to make any further edits.
            </ModalDescr>
          ),
          okText: 'Yes, proceed to delete',
          onOk: async () => {
            const res = await onDeletePromoCode?.(focusedId);
            res && setDelPopup(ModalLvl.success);
          },
          onCancel: () => setDelPopup(ModalLvl.closed),
        });
      }
    }

    if (delModalLvl === ModalLvl.success) {
      const item = tableView === TableViews.codes ? 'promo' : 'guest';
      Modal.success({
        title: `The guest has been deleted!`,
        content: <ModalDescr>You will not see the {item} now.</ModalDescr>,
        okText: 'Done',
        onOk: () => {
          setDelPopup(ModalLvl.closed);
        },
      });
    }
  }, [delModalLvl]);

  useEffect(() => {
    const email = promoCodes?.find((el) => el?.id === focusedId)?.email?.email;
    if (sendModalLvl === ModalLvl.confirm) {
      Modal.confirm({
        title: `Are you ready to ${sendText} this code to ${email}?`,
        content: (
          <ModalDescr>
            Please make sure the email address is correct. With this action, your guest will receive an email with the
            ticket. This action cannot be undone.
          </ModalDescr>
        ),
        okText: 'Yes',
        onOk: async () => {
          await send(focusedId);
          setSendPopup(ModalLvl.closed);
        },
        onCancel: () => setSendPopup(ModalLvl.closed),
      });
    }
  }, [sendModalLvl, focusedId]);
  useEffect(() => {
    setEditing('');
  }, [tableView]);
  const columns = getTableColumns({
    tableView: tableView,
    searchValue: searchValue,
    setSearchValue: setSearchValue,
    setIsSearchRes,
    codesState,
    eCodeSetState: handleEdit,
    searchPromoCodes: searchPromoCodes,
    focusId: focusedId,
    onSendCode: handleSend,
    setFocusId: setFocusedId,
    loading: loading,
    emailstate: guestState,
    eCodeState: eCodeState,
    editingId: editingId,
    onUpdateCode: onSave,
    setEditingId: setEditing,
    updateTarget: updateTargets,
    setDelPopup: setDelPopup,
    setEmail: handleSetEmail,
    setName: handleSetName,
    editingCodeStatus: editingCodeStatus,
    promoState,
    changePromoState,
    onUpdatePromo,
    totalCodes,
    usedCodes,
    onEmailPreview: loadEmailPreview,
  });

  return (
    <>
      <AboveTableLine>
        <TableViewSelect value={tableView} options={tableViewOptions} onChange={tableViewChange} />
        {isSearchRes && searchValue && (
          <SearchResult
            resQty={(promoCodes?.length || 1) - (tableView === TableViews.codes ? 0 : 1)}
            showResult={setIsSearchRes}
            searchPromoCodes={searchPromoCodes}
            value={searchValue}
            setValue={setSearchValue}
          />
        )}
        <DownloadCSV downloadCSV={downloadCSV} />
      </AboveTableLine>
      <TableWrapper>
        <ViewTable columns={columns} dataSource={promoCodes} showHeader={true} />
      </TableWrapper>
      {listLoading && <CenteredLoader />}
    </>
  );
};

const SearchResult: React.FC<SearchResultProps> = ({resQty, showResult, value, setValue, searchPromoCodes}) => {
  const onClearClick = () => {
    setValue('');
    searchPromoCodes('');
    showResult(false);
  };

  return (
    <SearchResultWrapper>
      <ResultLabel>{`${resQty} Results for "${value}"`}</ResultLabel>
      <ClearButton onClick={onClearClick}>Clear Search</ClearButton>
    </SearchResultWrapper>
  );
};
